import { css, resetButtonStyle } from '@eti/styles';
import type { ComponentPropsWithoutRef, ElementType, PropsWithChildren, ReactElement } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useScrollTo } from '@eti/utils';
import { useDirection } from '@eti/providers';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import isEmpty from 'lodash/isEmpty';
import { ConditionalWrapper } from '@eti/components';

const scrollableTabsWrapperStyles = css`
  position: relative;
  width: 100%;
`;

const tabsWrapperStyles = css`
  display: flex;
  list-style: none;
  -webkit-overflow-scrolling: touch; /* Important for iOS devices */
  -ms-overflow-style: none; /* IE and Edge */
  overflow-x: auto;
  scroll-snap-type: none;
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }

  white-space: nowrap;
`;

const caretButtonStyles = (isLeft: boolean, isVisible: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  background-color: #fff;
  border: none;
  bottom: 0;
  cursor: pointer;
  ${isLeft ? 'left: 0' : 'right: 0'};
  ${isLeft
    ? ' mask: linear-gradient(to right, black 75%, transparent 100%);'
    : ' mask: linear-gradient(to left, black 75%, transparent 100%);'};
  ${isVisible ? 'display: block' : 'display: none'};
  ${isLeft ? 'padding: 15px 24px 15px 6px' : 'padding: 15px 6px 15px 24px'};
  position: absolute;
  top: 0;

  & > svg {
    vertical-align: middle;
  }
`;

export type ScrollableTabsType = {
  isNavigation: boolean;
  scrollToElement: (index: number | null) => void;
};
interface ScrollableTabsProps {
  children: ReactElement;
  isNavigation?: boolean;
  selectedIndex?: number | null;
  role?: string;
}

const ScrollableTabsContext = createContext<ScrollableTabsType | null>(null);

const ScrollableTabs = ({
  children,
  isNavigation = false,
  selectedIndex,
  role,
}: ScrollableTabsProps) => {
  const offset = 50;
  const tabsContainerRef = useRef<HTMLDivElement & HTMLUListElement>(null);
  const { scrollHorizontal } = useScrollTo(tabsContainerRef);
  const { isRTL } = useDirection();
  const [maxScrollLeft, setMaxScrollLeft] = useState(0);
  const [isLeftCaretVisible, setIsLeftCaretVisible] = useState(false);
  const [isRightCaretVisible, setIsRightCaretVisible] = useState(false);
  const [rightBoundary, setRightBoundary] = useState(0);
  const [leftBoundary, setLeftBoundary] = useState(0);
  const [tabElements, setTabElements] = useState<Element[]>([]);

  const scrollToElement = useCallback(
    (index) => {
      if (tabsContainerRef.current && leftBoundary !== 0 && rightBoundary !== 0) {
        const activeElement = tabsContainerRef.current.children[index];
        if (activeElement.getBoundingClientRect().left < leftBoundary) {
          scrollHorizontal(
            'smooth',
            tabsContainerRef.current.scrollLeft -
              (leftBoundary - activeElement.getBoundingClientRect().left),
          );
        }

        if (activeElement.getBoundingClientRect().right > rightBoundary) {
          scrollHorizontal(
            'smooth',
            tabsContainerRef.current.scrollLeft +
              activeElement.getBoundingClientRect().right -
              rightBoundary,
          );
        }
      }
    },
    [tabsContainerRef, leftBoundary, rightBoundary, scrollHorizontal],
  );

  useEffect(() => {
    if (selectedIndex) {
      scrollToElement(selectedIndex);
    }
  }, [scrollToElement, selectedIndex]);

  const shouldShowLeftCaret = useCallback(() => {
    if (!tabsContainerRef?.current) {
      return false;
    }
    return isRTL
      ? Math.abs(tabsContainerRef.current.scrollLeft) < maxScrollLeft
      : tabsContainerRef.current.scrollLeft > 0;
  }, [isRTL, maxScrollLeft, tabsContainerRef]);

  const shouldShowRightCaret = useCallback(() => {
    if (!tabsContainerRef?.current) {
      return false;
    }
    return isRTL
      ? Math.abs(tabsContainerRef.current.scrollLeft) > 0
      : tabsContainerRef.current.scrollLeft < maxScrollLeft;
  }, [isRTL, maxScrollLeft, tabsContainerRef]);

  useEffect(() => {
    if (tabsContainerRef?.current) {
      setMaxScrollLeft(tabsContainerRef.current.scrollWidth - tabsContainerRef.current.clientWidth);
      setIsLeftCaretVisible(shouldShowLeftCaret());
      setIsRightCaretVisible(shouldShowRightCaret());
      setRightBoundary(tabsContainerRef.current.getBoundingClientRect().right - offset);
      setLeftBoundary(tabsContainerRef.current.getBoundingClientRect().left + offset);
      setTabElements(Array.from(tabsContainerRef.current.children));
    }
  }, [tabsContainerRef, shouldShowLeftCaret, shouldShowRightCaret]);

  const handleOnScroll = () => {
    setIsLeftCaretVisible(shouldShowLeftCaret());
    setIsRightCaretVisible(shouldShowRightCaret());
  };

  const handleCaretLeftClick = () => {
    const tabs = isRTL ? tabElements : tabElements.reverse();

    const leftHiddenTab = tabs.find((tab) => {
      return (
        Math.floor(tab.getBoundingClientRect().left) < Math.floor(leftBoundary) &&
        Math.floor(tab.getBoundingClientRect().right) < Math.floor(rightBoundary)
      );
    });

    if (tabsContainerRef.current && leftHiddenTab?.getBoundingClientRect()) {
      scrollHorizontal(
        'smooth',
        tabsContainerRef.current.scrollLeft -
          (rightBoundary - leftHiddenTab?.getBoundingClientRect().right),
      );
    }
  };

  const handleCaretRightClick = () => {
    const tabs = isRTL ? tabElements.reverse() : tabElements;

    const rightHiddenTab = tabs.find((tab) => {
      return (
        Math.floor(tab.getBoundingClientRect().right) > Math.floor(rightBoundary) &&
        Math.floor(tab.getBoundingClientRect().left) > Math.floor(leftBoundary)
      );
    });
    if (tabsContainerRef.current && rightHiddenTab?.getBoundingClientRect()) {
      scrollHorizontal(
        'smooth',
        tabsContainerRef.current.scrollLeft +
          rightHiddenTab.getBoundingClientRect().left -
          leftBoundary,
      );
    }
  };

  const WrapperComponent = isNavigation ? 'nav' : 'div';
  const TabsWrapperComponent = isNavigation ? 'ul' : 'div';

  return (
    <ScrollableTabsContext.Provider value={{ isNavigation, scrollToElement }}>
      <WrapperComponent className={scrollableTabsWrapperStyles}>
        <TabsWrapperComponent
          className={tabsWrapperStyles}
          data-testid="scrollable-tabs-wrapper"
          onScroll={handleOnScroll}
          ref={tabsContainerRef}
          role={role}
        >
          {children}
        </TabsWrapperComponent>
        <button
          aria-hidden
          className={caretButtonStyles(true, isLeftCaretVisible)}
          data-testid="left-caret"
          onClick={handleCaretLeftClick}
          tabIndex={-1}
          type="button"
        >
          <CaretLeft color="#737373" size={24} />
        </button>
        <button
          aria-hidden
          className={caretButtonStyles(false, isRightCaretVisible)}
          data-testid="right-caret"
          onClick={handleCaretRightClick}
          tabIndex={-1}
          type="button"
        >
          <CaretRight color="#737373" size={24} />
        </button>
      </WrapperComponent>
    </ScrollableTabsContext.Provider>
  );
};

const useScrollableTabs = (): ScrollableTabsType => {
  const context = useContext(ScrollableTabsContext);

  if (isEmpty(context)) {
    throw new Error('useScrollableTabs must be used within an ScrollableTabsProvider');
  }

  return { ...context };
};

type AsProp<C extends ElementType> = {
  as?: C;
};

type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProp<C extends ElementType, Props = {}> = PropsWithChildren<
  Props & AsProp<C> & Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>
>;

type TabProps = { index: number | null };
const Tab = <C extends ElementType = 'div'>({
  as,
  'data-testid': dataTestId,
  index,
  children,
  onClick,
  onFocus,
  ...restProps
}: PolymorphicComponentProp<C, TabProps>) => {
  const { isNavigation } = useScrollableTabs();
  const Component = as || 'div';

  const { scrollToElement } = useScrollableTabs();
  return (
    <ConditionalWrapper
      condition={isNavigation}
      wrapper={(wrapperChildren) => (
        <li data-testid={`${dataTestId}-list-item`}>{wrapperChildren}</li>
      )}
    >
      <Component
        {...restProps}
        data-testid={dataTestId}
        onClick={(...args) => {
          scrollToElement(index);
          onClick?.(args);
        }}
        onFocus={(...args) => {
          scrollToElement(index);
          onFocus?.(args);
        }}
      >
        {children}
      </Component>
    </ConditionalWrapper>
  );
};

ScrollableTabs.Tab = Tab;
export default ScrollableTabs;
